import { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  Typography,
} from '@mui/material';

import { Controller, useForm } from 'react-hook-form';
import { grey } from '@mui/material/colors';
import { useWeatherStation } from './api/getWeatherStation';
import { useCreateWeatherStation } from './api/createWeatherStation';
import { useUpdateWeatherStation } from './api/updateWeatherStation';
import { useDeleteWeatherStation } from './api/deleteWeatherStation';

interface Props {
  open: boolean;
  handleClose: () => void;
  stationId: number;
  updateStationId: (id: number) => void;
}

const initialFormState: {
  id: number;
  name: string;
  type: string;
  weatherStationSettings: Array<{
    settings_name: string;
    settings_value: string;
  }>;
} = {
  id: 0,
  name: '',
  type: '',
  weatherStationSettings: [],
};

const WeatherStationsDialog = ({
  open,
  handleClose,
  stationId,
  updateStationId,
}: Props) => {
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: initialFormState,
    mode: 'onChange',
  });

  const { data: station } = useWeatherStation({ stationId });
  const createStation = useCreateWeatherStation({});
  const updateStation = useUpdateWeatherStation({});
  const deleteStation = useDeleteWeatherStation({});

  useEffect(() => {
    if (station) reset(station);
    else reset(initialFormState);
  }, [reset, station]);

  const onSubmit = async () => {
    const stationForm = getValues();
    if (!stationForm.id) {
      await createStation.mutateAsync(stationForm);
    } else {
      const stationFormId = stationForm.id;
      if (stationFormId) {
        await updateStation.mutateAsync({
          stationId: stationFormId,
          data: stationForm,
        });
      }
    }
    handleClose();
  };

  const handleResetSettings = (type: string) => {
    if (type === '') setValue('weatherStationSettings', []);
    if (type.startsWith('WeatherLink'))
      setValue('weatherStationSettings', [
        { settings_name: 'api_key', settings_value: '' },
        { settings_name: 'station_id', settings_value: '' },
      ]);
  };

  const handleChangeSetting = (index: number, value: string) => {
    const [...currentSettings] = getValues('weatherStationSettings');
    currentSettings[index] = {
      settings_name: currentSettings[index].settings_name,
      settings_value: value,
    };
    setValue('weatherStationSettings', currentSettings);
  };

  const handleDelete = async () => {
    await deleteStation.mutateAsync(stationId);
    reset(initialFormState);
    updateStationId(0);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        {watch('id') ? 'Edit Weather Station' : 'New Weather Station'}
      </DialogTitle>
      <DialogContent>
        <Grid sx={{ pt: '1rem' }} container spacing={1} direction='column'>
          <Controller
            name='type'
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Grid item>
                <FormControl fullWidth>
                  <InputLabel required id='type-select-label'>
                    Weather Station Type
                  </InputLabel>
                  <Select
                    labelId='type-select-label'
                    label='Weather Station Type'
                    id='type'
                    name='type'
                    onChange={(e) => {
                      onChange(e);
                      handleResetSettings(e.target.value);
                    }}
                    value={value}
                    error={!!errors.type}
                  >
                    <MenuItem value={''}>
                      <em>None</em>
                    </MenuItem>
                    <MenuItem
                      value={'WeatherLink (Vantage Pro2 Plus, Live ISS)'}
                    >
                      WeatherLink (Vantage Pro2 Plus, Live ISS)
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          />
          <Controller
            name='name'
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Grid item>
                <TextField
                  id='name'
                  label='Name'
                  name='name'
                  value={value}
                  onChange={onChange}
                  margin='dense'
                  fullWidth
                  required
                  error={!!errors.name}
                />
              </Grid>
            )}
          />
          <Controller
            name='weatherStationSettings'
            control={control}
            rules={{
              validate: (
                settings: { settings_name: string; settings_value: string }[]
              ) => settings.every((setting) => !!setting.settings_value),
            }}
            render={({ field: { onChange, value } }) => (
              <>
                {value.map((setting, idx) => (
                  <Grid item key={idx}>
                    <TextField
                      id={value[idx].settings_name}
                      label={value[idx].settings_name
                        .split('_')
                        .map((word) =>
                          word[0].toUpperCase().concat(word.slice(1))
                        )
                        .join(' ')}
                      name={value[idx].settings_name}
                      value={value[idx].settings_value}
                      onChange={(e) => handleChangeSetting(idx, e.target.value)}
                      margin='dense'
                      fullWidth
                      required
                      error={!!errors.weatherStationSettings}
                    />
                  </Grid>
                ))}
              </>
            )}
          />
          {watch('type').startsWith('WeatherLink') && (
            <Grid item>
              <Typography color={grey[500]}>
                To complete the setup of your WeatherLink weather station, add
                your API Secret to your .env file in the Spectras API root
                directory, using the format
                WEATHERLINK_SECRET_KEY=your_secret_key, and then restart the
                Spectras API.
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        {stationId !== 0 && (
          <Button onClick={handleDelete} color='error' variant='contained'>
            Delete
          </Button>
        )}
        <Button
          onClick={() => handleClose()}
          color='secondary'
          variant='contained'
        >
          Cancel
        </Button>
        <Button
          color='primary'
          onClick={() => handleSubmit(onSubmit)()}
          variant='contained'
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WeatherStationsDialog;
